import { useState, useEffect } from 'react';
import { api } from '../api/client.js';

export default function RoleMappings() {
  const [mappings, setMappings] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ groupEmail: '', role: 'ADMIN', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get('/api/role-mappings');
      setMappings(r.data.mappings || []);
      setAvailableGroups(r.data.availableGroups || []);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/api/role-mappings', {
        groupEmail: formData.groupEmail,
        role: formData.role,
        notes: formData.notes || null
      });
      setFormData({ groupEmail: '', role: 'ADMIN', notes: '' });
      setShowForm(false);
      await refresh();
    } catch (e) {
      setError(e.response?.data?.message || e.response?.data?.error || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id, groupEmail) => {
    if (!window.confirm(`Remove mapping for ${groupEmail}? Members will lose this role on the next sync.`)) {
      return;
    }
    try {
      await api.delete(`/api/role-mappings/${id}`);
      await refresh();
    } catch (e) {
      setError(e.response?.data?.message || e.response?.data?.error || e.message);
    }
  };

  // Filter out already-mapped groups from the dropdown (one mapping per group)
  const mappedEmails = new Set(mappings.map((m) => m.groupEmail));
  const unmappedGroups = availableGroups.filter((g) => !mappedEmails.has(g.email));

  if (loading) {
    return (
      <div className="bg-white border border-border rounded-xl p-6 mb-6 text-sm text-muted">
        Loading role mappings…
      </div>
    );
  }

  return (
    <div className="bg-white border border-border rounded-xl p-6 mb-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-base font-medium mb-1">Role mappings</h2>
          <p className="text-xs text-muted">
            Map Google Workspace groups to launcher roles. Members of mapped groups
            get their role assigned automatically on each Directory sync. Without any
            mappings configured, roles must be set manually in the database.
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            disabled={unmappedGroups.length === 0}
            className="text-sm bg-ink text-white rounded-lg px-4 py-2 disabled:opacity-50 whitespace-nowrap"
          >
            + Add mapping
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="bg-neutral-50 rounded-xl p-4 mb-4 space-y-3">
          <div>
            <label className="block text-xs text-muted mb-1">Group</label>
            <select
              required
              value={formData.groupEmail}
              onChange={(e) => setFormData({ ...formData, groupEmail: e.target.value })}
              className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-white"
            >
              <option value="">Select a group…</option>
              {unmappedGroups.map((g) => (
                <option key={g.email} value={g.email}>
                  {g.name} — {g.email} ({g.memberCount} members)
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-muted mb-1">Role</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-white"
            >
              <option value="ADMIN">ADMIN — full launcher access</option>
              <option value="MEMBER">MEMBER — standard user access</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-muted mb-1">
              Notes <span className="text-muted">(optional)</span>
            </label>
            <input
              type="text"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full text-sm border border-border rounded-lg px-3 py-2 bg-white"
              placeholder="e.g. Onboarded 2026-05; covers all engineering managers"
              maxLength={500}
            />
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setFormData({ groupEmail: '', role: 'ADMIN', notes: '' });
                setError(null);
              }}
              className="text-sm text-muted px-4 py-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !formData.groupEmail}
              className="text-sm bg-ink text-white rounded-lg px-4 py-2 disabled:opacity-50"
            >
              {submitting ? 'Saving…' : 'Save mapping'}
            </button>
          </div>
        </form>
      )}

      {mappings.length === 0 && !showForm && (
        <div className="border border-dashed border-border rounded-xl p-8 text-center text-sm text-muted">
          No role mappings yet. Add one to assign launcher roles based on group
          membership.
        </div>
      )}

      {mappings.length > 0 && (
        <div className="border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-muted">
              <tr>
                <th className="text-left font-medium px-4 py-3">Group</th>
                <th className="text-left font-medium px-4 py-3">Role</th>
                <th className="text-left font-medium px-4 py-3">Members</th>
                <th className="text-left font-medium px-4 py-3">Notes</th>
                <th className="text-right font-medium px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {mappings.map((m) => (
                <tr key={m.id} className="border-t border-border">
                  <td className="px-4 py-3">
                    <div className="font-medium">{m.groupName || m.groupEmail}</div>
                    {m.groupName && (
                      <div className="text-xs text-muted">{m.groupEmail}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <RoleBadge role={m.role} />
                  </td>
                  <td className="px-4 py-3 text-muted">{m.memberCount}</td>
                  <td className="px-4 py-3 text-muted">{m.notes || '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => remove(m.id, m.groupEmail)}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RoleBadge({ role }) {
  const styles = {
    ADMIN: { bg: '#FAEEDA', fg: '#633806' },
    MEMBER: { bg: '#E1F5EE', fg: '#085041' }
  };
  const s = styles[role] || styles.MEMBER;
  return (
    <span
      className="inline-block text-xs font-medium px-2 py-0.5 rounded-full"
      style={{ background: s.bg, color: s.fg }}
    >
      {role}
    </span>
  );
}